import { Link } from "react-router";
import classNames from "classnames";

import AuthMixin from "../../mixins/authenticationMixin";
import AppRoutes from "../../routes/routes";
import AppStore from "../../stores/stores";

import "./checklist-context-marketing.scss";

// the page remounts for every checklist, so keep each chapter's topics for the
// session rather than refetching them when moving between siblings
const chapterTopicsCache = {};

/**
 * "See also" sidebar for a checklist: the other checklists in its topic (already
 * part of the checklist response) and the rest of its chapter (one small request)
 */
class ChecklistContext extends React.Component {
  constructor(props) {
    super(props);
    const chapterId = props.chapter && props.chapter.id;
    this.state = {
      topics: chapterTopicsCache[chapterId] || null,
      error: false,
    };
  }

  componentDidMount() {
    const { chapter, userApiRequest } = this.props;
    const chapterId = chapter && chapter.id;

    if (!chapterId || this.state.topics) return;

    AppStore.getChapterTopics(chapterId, userApiRequest, {
      onSuccess: ({ data }) => {
        const topics = (data || []).map((topic) => ({
          slug: topic._slugs[0],
          title: topic.title,
          checklists: (topic.select_checklists || []).map((checklist) => ({
            slug: checklist._slugs[0],
            title: checklist.title,
          })),
        }));
        chapterTopicsCache[chapterId] = topics;
        if (!this.unmounted) this.setState({ topics });
      },
      onError: () => {
        if (!this.unmounted) this.setState({ error: true });
      },
    });
  }

  componentWillUnmount() {
    this.unmounted = true;
  }

  renderTopicChecklists() {
    const { related, topic, current } = this.props;
    if (!related || related.length < 2) return null;

    return (
      <section className="see-also-card">
        <div className="see-also-header">
          <span className="see-also-eyebrow">In this topic</span>
          <h2 className="see-also-title">{topic.title}</h2>
        </div>
        <ul className="see-also-checklists">
          {related.map((checklist) => {
            const slug = checklist._slugs[0];
            const isCurrent = slug === current;
            return (
              <li key={slug} className={classNames({ "is-current": isCurrent })}>
                <Link to={`${AppRoutes.checklists}${slug}`} aria-current={isCurrent ? "page" : undefined}>
                  {checklist.title}
                </Link>
              </li>
            );
          })}
        </ul>
      </section>
    );
  }

  renderChapterTopics() {
    const { chapter, topic } = this.props;
    const { topics, error } = this.state;

    if (error || !chapter || !chapter.id) return null;

    const otherTopics = topics && topics.filter((item) => item.slug !== topic.slug && item.checklists.length);
    if (otherTopics && !otherTopics.length) return null;

    return (
      <section className="see-also-card">
        <div className="see-also-header">
          <span className="see-also-eyebrow">More in this chapter</span>
          <h2 className="see-also-title is-chapter">{chapter.title}</h2>
        </div>
        {otherTopics ? (
          <ul className="see-also-topics">
            {otherTopics.map((item) => (
              <SeeAlsoTopic key={item.slug} topic={item} />
            ))}
          </ul>
        ) : (
          <div className="see-also-skeleton" aria-hidden="true">
            <span /><span /><span />
          </div>
        )}
        {chapter.slug && (
          <div className="see-also-footer">
            <Link to={`${AppRoutes.chapters}${chapter.slug}`}>
              Browse the whole chapter
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </Link>
          </div>
        )}
      </section>
    );
  }

  render() {
    const { topic } = this.props;
    if (!topic) return null;

    return (
      <div className="see-also" role="navigation" aria-label="See also">
        {this.renderTopicChecklists()}
        {this.renderChapterTopics()}
      </div>
    );
  }
}

/**
 * collapsible topic in the chapter list. links stay in the DOM while collapsed
 * so they remain crawlable
 */
class SeeAlsoTopic extends React.Component {
  constructor(props) {
    super(props);
    this.state = { open: false };
    this.toggleOpen = this.toggleOpen.bind(this);
  }

  toggleOpen() {
    this.setState(({ open }) => ({ open: !open }));
  }

  render() {
    const { topic } = this.props;
    const { open } = this.state;

    return (
      <li className={classNames("see-also-topic", { "is-open": open })}>
        <button type="button" className="see-also-toggle" aria-expanded={open} onClick={this.toggleOpen}>
          <span className="see-also-topic-title">{topic.title}</span>
          <span className="see-also-count">{topic.checklists.length}</span>
          <svg className="see-also-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="9 6 15 12 9 18" />
          </svg>
        </button>
        <ul className="see-also-checklists" hidden={!open}>
          {topic.checklists.map((checklist) => (
            <li key={checklist.slug}>
              <Link to={`${AppRoutes.checklists}${checklist.slug}`}>{checklist.title}</Link>
            </li>
          ))}
        </ul>
      </li>
    );
  }
}

export default AuthMixin(ChecklistContext);
